import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faCircleDot } from '@fortawesome/free-solid-svg-icons';
import useUserMenuItems from './useUserMenuItems';

const Sidebar = ({ isOpen }) => {
  const [openDropdownKey, setOpenDropdownKey] = useState(null);
  const location = useLocation();
  const { menuItems: menuSource } = useUserMenuItems();

  const normalizePath = (path) => {
    if (!path) return '';
    // Remove trailing slashes except for root.
    if (path.length > 1) return path.replace(/\/+$/, '');
    return path;
  };

  const currentPath = normalizePath(location.pathname);

  const isPathMatch = (path) => {
    const p = normalizePath(path);
    if (!p) return false;
    return currentPath === p || currentPath.startsWith(`${p}/`);
  };

  // If multiple menu paths match (e.g. "/user/modules" and "/user/modules/menus"),
  // activate only the most specific (longest) one.
  const bestMatch = (() => {
    let best = '';
    for (const group of menuSource) {
      for (const item of group.items) {
        if (isPathMatch(item.to) && normalizePath(item.to).length > best.length) {
          best = normalizePath(item.to);
        }
        if (item.subItems?.length) {
          for (const subItem of item.subItems) {
            if (isPathMatch(subItem.to) && normalizePath(subItem.to).length > best.length) {
              best = normalizePath(subItem.to);
            }
          }
        }
      }
    }
    return best;
  })();

  const isActive = (path) => normalizePath(path) === bestMatch;
  const toggleDropdown = (key) => {
    setOpenDropdownKey(openDropdownKey === key ? null : key);
  };
  const isDropdownActive = (subItems) =>
    subItems.some((subItem) => isPathMatch(subItem.to));

  useEffect(() => {
    setOpenDropdownKey(null);
  }, [location]);

  useEffect(() => {
    if (!isOpen) setOpenDropdownKey(null);
  }, [isOpen]);

  return (
    <>
      <aside
        id="logo-sidebar"
        className={`h-full pt-16 transition-all bg-white border-r border-gray-100 dark:bg-gray-800 dark:border-gray-700 ${!isOpen ? 'w-12 overflow-x-hidden' : 'w-64'}`}
        aria-label="Sidebar"
      >
        <div className={`h-full ${!isOpen ? "px-1" : 'px-3 '} pb-4 overflow-y-auto bg-white dark:bg-gray-800 scrollbar-custom`}>
          <ul className="space-y-2 text-[14px]">
            {menuSource.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className={`pb-2 ${!isOpen ? "" : 'border-b border-gray-200 dark:border-gray-700'}  ${groupIndex === 0 ? 'pt-3' : !isOpen ? "" : 'pt-1'}`}
              >
                <h4 className={`pl-2 ${!isOpen ? "" : 'mb-2'}  font-semibold text-[15px] tracking-wide text-gray-500 dark:text-gray-400`}>
                  {isOpen ? group.group : ""}
                </h4>
                {group.items.map((item, index) => {
                  const dropdownKey = `${groupIndex}-${index}`;
                  const isOpenNow = openDropdownKey === dropdownKey;
                  const isActiveNow = item.isDropdown ? (isOpenNow || isDropdownActive(item.subItems)) : false;

                  return (
                    <li key={index} className='my-2'>
                      {item.isDropdown ? (
                        <>
                          <button
                            onClick={() => toggleDropdown(dropdownKey)}
                            title={!isOpen ? item.label : undefined}
                            aria-expanded={isActiveNow}
                            className={`flex items-center w-full p-2 rounded-lg group transition-colors ${isActiveNow
                              ? 'font-medium text-white bg-gradient-to-r from-[#C04C2A] to-[#4A1B13]'
                              : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                          >
                            <FontAwesomeIcon
                              icon={item.icon}
                              className={`${!isOpen ? "w-5 h-5" : "w-4 h-4"} ${isActiveNow ? "text-white" : "text-two"} transition duration-75 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white`}
                            />
                            {isOpen && <span className="ms-3 whitespace-nowrap">{item.label}</span>}
                            <FontAwesomeIcon
                              icon={isOpenNow ? faChevronDown : faChevronRight}
                              className={`ml-auto font-thin w-3 h-3 transition-transform ${isOpenNow ? 'rotate-30' : ''} ${isActiveNow ? "text-white/80" : "text-gray-500"}`}
                            />
                          </button>
                          {isOpen && isActiveNow && (
                            <ul className="pl-5 mt-2 space-y-1">
                              {item.subItems.map((subItem, subIndex) => (
                                <li key={subIndex}>
                                  <Link
                                    to={subItem.to}
                                    title={!isOpen ? subItem.label : undefined}
                                    className={`flex items-center p-2 text-sm rounded-lg group transition-colors ${isActive(subItem.to)
                                      ? 'font-medium bg-orange-50 text-[#C04C2A] dark:bg-gray-700 dark:text-white'
                                      : 'text-gray-500 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                                      }`}
                                  >
                                    <FontAwesomeIcon
                                      icon={subItem.icon || faCircleDot}
                                      className={`w-3 h-3 mr-2 ${isActive(subItem.to)
                                        ? 'text-[#C04C2A] dark:text-white'
                                        : 'text-gray-400 dark:text-gray-400'
                                        }`}
                                    />
                                    <span>{subItem.label}</span>
                                    {subItem.badge ? (
                                      <span className="inline-flex items-center justify-center px-2 ms-3 text-xs font-medium text-gray-800 bg-gray-100 rounded-full dark:bg-gray-700 dark:text-gray-300">
                                        {subItem.badge}
                                      </span>
                                    ) : null}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      ) : (
                        <Link
                          to={item.to}
                          title={!isOpen ? item.label : undefined}
                          className={`flex items-center p-2 rounded-lg group transition-colors ${isActive(item.to)
                            ? 'text-white font-medium bg-gradient-to-r from-[#C04C2A] to-[#4A1B13]'
                            : 'text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                        >
                          <FontAwesomeIcon
                            icon={item.icon}
                            className={`
                                  ${!isOpen ? "w-5 h-5" : "w-4 h-4"} 
                                  ${isActive(item.to) ? "text-white" : "text-two"} 
                                  transition duration-75 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white
                                `}
                          />
                          {isOpen && (
                            <span className="ms-3 whitespace-nowrap">
                              {item.label}
                            </span>
                          )}
                          {item.badge && (
                            <span className="inline-flex items-center justify-center px-2 ms-3 text-sm font-medium text-gray-800 bg-gray-100 rounded-full dark:bg-gray-700 dark:text-gray-300">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </div>
            ))}
          </ul>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
